/**
 * Rider directory data access.
 *
 * Deactivating or restoring a rider changes what the remittance board will
 * accrue next time, and may leave a bike showing a former holder — so both
 * invalidate the rider-finance queries alongside the directory.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  DeactivateRiderInput,
  RemittanceDebtClearanceInput,
  RemittanceDebtClearanceView,
  RiderDebt,
  RiderRemovalResult,
  RiderDirectoryRow,
  RiderEntanglements,
  RiderFilters,
  RiderInput,
  RiderUpdate,
  UnmatchedRiderName,
} from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';

const qs = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) search.set(k, v);
  const s = search.toString();
  return s ? `?${s}` : '';
};

export function useRiderDirectory(
  filters: RiderFilters = {},
): UseQueryResult<{ data: RiderDirectoryRow[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderDirectory', path(''), filters],
    queryFn: () =>
      api.get<{ data: RiderDirectoryRow[] }>(
        path(
          `/rider-directory${qs({ status: filters.status, branchId: filters.branchId, search: filters.search, ownership: filters.ownership })}`,
        ),
      ),
    staleTime: 30_000,
  });
}

/** Owner-only: names on deliveries that match no rider. */
export function useUnmatchedNames(enabled: boolean): UseQueryResult<{ data: UnmatchedRiderName[] }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderDirectory', path(''), 'unmatched'],
    queryFn: () => api.get<{ data: UnmatchedRiderName[] }>(path('/rider-directory/unmatched')),
    enabled,
    staleTime: 60_000,
  });
}

export function useRiderEntanglements(
  id: string | null,
): UseQueryResult<{ data: RiderEntanglements }> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderDirectory', path(''), id, 'entanglements'],
    queryFn: () => api.get<{ data: RiderEntanglements }>(path(`/rider-directory/${id!}/entanglements`)),
    enabled: id !== null,
  });
}

function useInvalidate(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['riderDirectory'] });
    // A rider's state changes what the next accrual charges and who a bike
    // shows as holding it.
    await client.invalidateQueries({ queryKey: ['riderFinance'] });
    await client.invalidateQueries({ queryKey: ['logistics'] });
  };
}

export function useCreateRider(): UseMutationResult<
  { data: RiderDirectoryRow },
  Error,
  RiderInput
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input) => api.post<{ data: RiderDirectoryRow }>(path('/rider-directory'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateRider(): UseMutationResult<
  { data: RiderDirectoryRow },
  Error,
  { id: string; input: RiderUpdate }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.patch<{ data: RiderDirectoryRow }>(path(`/rider-directory/${id}`), input),
    onSuccess: invalidate,
  });
}

export function useDeactivateRider(): UseMutationResult<
  { data: RiderDirectoryRow },
  Error,
  { id: string; input: DeactivateRiderInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.post<{ data: RiderDirectoryRow }>(path(`/rider-directory/${id}/deactivate`), input),
    onSuccess: invalidate,
  });
}

export function useRestoreRider(): UseMutationResult<
  { data: RiderDirectoryRow },
  Error,
  string
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id) => api.post<{ data: RiderDirectoryRow }>(path(`/rider-directory/${id}/restore`), {}),
    onSuccess: invalidate,
  });
}

export function useRiderCurrency(): string {
  const { business } = useAuth();
  return business?.currencyCode ?? 'NGN';
}

/** Remove a rider: deleted when nothing is on record for them, archived otherwise. */
export function useRemoveRider(): UseMutationResult<
  { data: RiderRemovalResult },
  Error,
  { id: string; input: DeactivateRiderInput }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }) =>
      api.post<{ data: RiderRemovalResult }>(path(`/rider-directory/${id}/remove`), input),
    onSuccess: invalidate,
  });
}

/** What a rider owes, week by week, and the clearances made before. */
export function useRiderDebt(id: string | null): UseQueryResult<RiderDebt> {
  const path = useBusinessPath();
  return useQuery({
    queryKey: ['riderFinance', 'riderDebt', path(''), id],
    queryFn: async () => (await api.get<{ data: RiderDebt }>(path(`/rider-finance/riders/${id!}/debt`))).data,
    enabled: id !== null,
  });
}

/** Clear part or all of what a rider owes. A write-off: never a payment, never revenue. */
export function useClearRiderDebt(): UseMutationResult<
  { data: RemittanceDebtClearanceView; replayed: boolean },
  Error,
  { riderId: string; input: RemittanceDebtClearanceInput }
> {
  const path = useBusinessPath();
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ riderId, input }) =>
      api.post<{ data: RemittanceDebtClearanceView; replayed: boolean }>(
        path(`/rider-finance/riders/${riderId}/debt-clearances`),
        input,
      ),
    onSuccess: async () => {
      await invalidate();
      await client.invalidateQueries({ queryKey: ['finance'] });
      await client.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
